import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { useOrganization } from '../hooks/useOrganization';
import { formatDate } from '../utils/dateFormat';

const AccruedExpenses = () => {
  const { organization } = useOrganization();
  const [accruedExpenses, setAccruedExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAccruedExpenses = useCallback(async () => {
    try {
      const response = await axios.get(`/api/accrued-expenses?status=${statusFilter}`);
      setAccruedExpenses(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch accrued expenses');
    }
  }, [statusFilter]);

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get('/api/accounts/chart-of-accounts?type=EXPENSE');
      setExpenseCategories(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch expense categories');
    }
  };

  useEffect(() => {
    fetchAccruedExpenses();
    fetchExpenseCategories();
  }, [fetchAccruedExpenses]);

  const handleCreateAccruedExpense = async (formData) => {
    try {
      await axios.post('/api/accrued-expenses', formData);
      toast.success('Accrued expense created successfully');
      setShowForm(false);
      fetchAccruedExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create accrued expense');
    }
  };

  const handlePayment = async (formData) => {
    try {
      await axios.post(`/api/accrued-expenses/${selectedExpense.id}/pay`, formData);
      toast.success('Payment recorded successfully');
      setShowPaymentForm(false);
      setSelectedExpense(null);
      fetchAccruedExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    }
  };

  const AccruedExpenseForm = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      expenseCategoryId: '',
      description: '',
      amount: '',
      accrualDate: new Date().toISOString().split('T')[0],
      expectedPaymentDate: '',
      notes: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Add Accrued Expense</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Expense Category</label>
            <select
              value={formData.expenseCategoryId}
              onChange={(e) => setFormData({...formData, expenseCategoryId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select Expense Category</option>
              {expenseCategories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="e.g., Unpaid salary for October"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Accrual Date</label>
              <input
                type="date"
                value={formData.accrualDate}
                onChange={(e) => setFormData({...formData, accrualDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Expected Payment Date</label>
            <input
              type="date"
              value={formData.expectedPaymentDate}
              onChange={(e) => setFormData({...formData, expectedPaymentDate: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              rows="3"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Create Accrued Expense
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const PaymentForm = ({ expense, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      paymentDate: new Date().toISOString().split('T')[0],
      notes: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <p><strong>Description:</strong> {expense?.description}</p>
            <p><strong>Category:</strong> {expense?.category?.name}</p>
            <p><strong>Amount:</strong> {formatCurrency(expense?.amount, organization?.currency_code)}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Payment Date</label>
              <input
                type="date"
                value={formData.paymentDate}
                onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                rows="2"
              />
            </div>
            <div className="flex space-x-3">
              <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                Record Payment
              </button>
              <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-800';
      default: return 'bg-orange-100 text-orange-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Accrued Expenses</h2>
        <div className="flex space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="all">All Status</option>
            <option value="ACCRUED">Accrued</option>
            <option value="PAID">Paid</option>
          </select>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Accrued Expense
          </button>
        </div>
      </div>

      {showForm ? (
        <AccruedExpenseForm onSubmit={handleCreateAccruedExpense} onCancel={() => setShowForm(false)} />
      ) : (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accrual Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {accruedExpenses.map(expense => (
                  <tr key={expense.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{expense.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{expense.category?.name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(expense.amount, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(expense.accrual_date).replace(/\s/g, '-')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {expense.expected_payment_date ? formatDate(expense.expected_payment_date).replace(/\s/g, '-') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(expense.status)}`}>
                        {expense.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {expense.status === 'ACCRUED' && (
                        <button
                          onClick={() => { setSelectedExpense(expense); setShowPaymentForm(true); }}
                          className="text-green-600 hover:text-green-800"
                        >
                          Pay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showPaymentForm && selectedExpense && (
        <PaymentForm
          expense={selectedExpense}
          onSubmit={handlePayment}
          onCancel={() => { setShowPaymentForm(false); setSelectedExpense(null); }}
        />
      )}
    </div>
  );
};

export default AccruedExpenses;